import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import Apartment from "@mui/icons-material/Apartment";
import ArrowBack from "@mui/icons-material/ArrowBack";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import Business from "@mui/icons-material/Business";
import EditOutlined from "@mui/icons-material/EditOutlined";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Search from "@mui/icons-material/Search";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import ShareIcon from "@mui/icons-material/Share";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dashboardAppTheme } from "../../../theme/dashboardAppTheme";
import AlertComponent from "../../subcomponents/AlertComponent";
import { PreLoader } from "../../subcomponents/Preloader";
import "../../styles/popupStyle.css";
import "../../styles/userAdminPage.css";
import { AGILYSYS_LOGO_URL } from "../../../constants/branding";
import { DEFAULT_USER_ROLES } from "../../../constants/userRoles";
import { WorkflowRoleCode } from "../../../constants/workflow";
import {
  ProductEnum,
  ProductMap,
  SearchParams,
} from "../../../constants/login";
import { validateEmail } from "../../actions/validations";
import {
  checkAuthStatus,
  createOnboardingUser,
  customerToCreatePayload,
  formValueToImplementationTeamEmails,
  getCustomers,
  getOnboardingUsers,
  getUserRoles,
  implementationTeamEmailsToFormValue,
  parseCustomers,
  normalizeRoleCodeToCatalog,
  parseOnboardingUsers,
  parseUserRoles,
  sendCredentialsEmail,
  updateOnboardingUser,
  type OnboardingCustomer,
  type OnboardingUser,
  type OnboardingUserRole,
  type UpdateOnboardingUserBody,
} from "../../services/requests";

type ShareCredentialSnapshot = {
  username: string;
  password: string;
  tenantId: number;
  propertyId: number;
  productId: number;
  email: string;
  firstName: string;
  lastName: string;
  customerName: string;
};

const primaryProductId = (productIds: number[], fallback: number): number =>
  productIds[0] ?? fallback;

const shareSnapshotToText = (s: ShareCredentialSnapshot): string =>
  [
    `username: ${s.username}`,
    `password: ${s.password}`,
    `tenantId: ${s.tenantId}`,
    `propertyId: ${s.propertyId}`,
  ].join("\n");

const productOptions = (Object.entries(ProductMap) as [string, number][])
  .map(([label, value]) => ({ label, value }))
  .sort((a, b) => a.label.localeCompare(b.label));

const PRODUCT_IDS = productOptions.map((o) => o.value);

/** Username: letters, digits, underscore and hyphen only; no spaces. */
const USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;
const CUSTOMER_CODE_REGEX = /^[a-zA-Z0-9_-]+$/;
const MAX_CUSTOMER_CODE_LENGTH = 16;

/** Derive a unique-ish customer code from the display name (base + random digits). */
const generateCustomerCodeFromName = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "";

  const words = trimmed.split(/\s+/).filter(Boolean);
  const strip = (s: string) => s.replace(/[^a-zA-Z0-9]/g, "");

  let base = "";
  if (words.length === 1) {
    base = strip(words[0]).toUpperCase();
  } else {
    const first = strip(words[0]).toUpperCase();
    const acronym = words
      .map((w) => strip(w).charAt(0))
      .filter(Boolean)
      .join("")
      .toUpperCase();
    base =
      first.length >= 3
        ? first
        : `${acronym}${strip(words[0]).slice(1)}`.toUpperCase();
  }

  if (!base) return "";

  base = base.slice(0, 12);
  const digits = "23456789";
  let suffix = "";
  for (let i = 0; i < 3; i += 1) {
    suffix += digits[Math.floor(Math.random() * digits.length)];
  }

  return `${base}${suffix}`.slice(0, MAX_CUSTOMER_CODE_LENGTH);
};

const withImplementationEmailsFromText = (
  customer: OnboardingCustomer,
  implEmailsText: string,
): OnboardingCustomer => ({
  ...customer,
  implementationTeamEmails: formValueToImplementationTeamEmails(implEmailsText),
});

const emptyCustomerDraft = (): OnboardingCustomer => ({
  code: "",
  name: "",
  email: "",
  implementationTeamEmails: [],
  technicalProjectManagerEmail: "",
  projectManagerEmail: "",
});

const parseApiResponseBody = async (
  res: Response,
): Promise<{ ok: boolean; status: number; parsed: unknown }> => {
  const bodyText = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(bodyText);
  } catch {
    parsed = bodyText;
  }
  return { ok: res.ok, status: res.status, parsed };
};

const apiErrorMessage = (
  parsed: unknown,
  status: number,
  fallback: string,
): string =>
  typeof parsed === "string" && parsed.trim()
    ? parsed
    : `${fallback} (${status})`;

const isSuccessStatus = (status: number): boolean =>
  status === 200 || status === 201;

const MAX_TENANT_PROPERTY_DIGITS = 4;
const MAX_TENANT_PROPERTY_VALUE = 10 ** MAX_TENANT_PROPERTY_DIGITS - 1;

const parseTenantPropertyDigits = (raw: string): number => {
  const digits = raw.replace(/\D/g, "").slice(0, MAX_TENANT_PROPERTY_DIGITS);
  if (digits === "") return 0;
  return Math.min(MAX_TENANT_PROPERTY_VALUE, parseInt(digits, 10));
};

const generateAlphanumericPassword = (length = 8): string => {
  const letters = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const pool = letters + digits;
  const chars: string[] = [];
  chars.push(letters[Math.floor(Math.random() * letters.length)]);
  chars.push(digits[Math.floor(Math.random() * digits.length)]);
  for (let i = chars.length; i < length; i += 1) {
    chars.push(pool[Math.floor(Math.random() * pool.length)]);
  }
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const productIdsFromTenants = (
  tenants: OnboardingUser["tenants"],
  validIds: number[],
): number[] => {
  const seen = new Set<number>();
  const ids: number[] = [];
  for (const t of tenants) {
    if (validIds.includes(t.productId) && !seen.has(t.productId)) {
      seen.add(t.productId);
      ids.push(t.productId);
    }
  }
  return ids;
};

const userOptionLabel = (u: OnboardingUser): string => {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  const cust = u.customer.code || u.customer.name;
  return [`#${u.id}`, u.username, name, cust ? `(${cust})` : ""]
    .filter(Boolean)
    .join(" — ");
};

const userMatchesSearch = (u: OnboardingUser, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    String(u.id),
    u.username,
    u.email,
    u.firstName,
    u.lastName,
    u.customer.code,
    u.customer.name,
    u.customer.email,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const customerMatches = (
  a: OnboardingCustomer,
  b: OnboardingCustomer,
): boolean => a.code.trim().toLowerCase() === b.code.trim().toLowerCase();

const withSelectedCustomerInOptions = (
  options: OnboardingCustomer[],
  selected: OnboardingCustomer | null,
): OnboardingCustomer[] => {
  if (!selected?.code.trim()) return options;
  if (options.some((c) => customerMatches(c, selected))) return options;
  return [selected, ...options];
};

const resolveCustomerFromList = (
  selected: OnboardingCustomer | null,
  options: OnboardingCustomer[],
): OnboardingCustomer | null => {
  if (!selected) return null;
  return options.find((c) => customerMatches(c, selected)) ?? selected;
};

const upsertCustomerInList = (
  list: OnboardingCustomer[],
  customer: OnboardingCustomer,
): OnboardingCustomer[] => {
  if (list.some((c) => customerMatches(c, customer))) return list;
  return [...list, customer];
};

const mergeCustomerForEdit = (
  raw: OnboardingUser["customer"],
  customers: OnboardingCustomer[],
): OnboardingCustomer => {
  const match = customers.find(
    (c) =>
      c.code === raw.code ||
      (raw.id != null && c.id === raw.id) ||
      (c.name && c.name === raw.name),
  );
  if (!match) return raw;
  const pick = (fromRaw: string, fromMatch: string) =>
    fromRaw.trim() ? fromRaw : fromMatch;
  const pickEmails = (fromRaw: string[], fromMatch: string[]) =>
    fromRaw.length > 0 ? fromRaw : fromMatch;
  return {
    ...match,
    ...raw,
    email: pick(raw.email, match.email),
    implementationTeamEmails: pickEmails(
      raw.implementationTeamEmails,
      match.implementationTeamEmails,
    ),
    technicalProjectManagerEmail: pick(
      raw.technicalProjectManagerEmail,
      match.technicalProjectManagerEmail,
    ),
    projectManagerEmail: pick(
      raw.projectManagerEmail,
      match.projectManagerEmail,
    ),
  };
};

const roleIdForCode = (
  code: string,
  roles: OnboardingUserRole[],
): number | undefined => {
  const normalized = code.trim().toLowerCase();
  const match = roles.find((r) => r.code.trim().toLowerCase() === normalized);
  return match && match.id > 0 ? match.id : undefined;
};

const userRoleFieldsForCode = (
  code: string,
  roles: OnboardingUserRole[],
): { userRoleCode: string; userRoleId?: number } => {
  const roleId = roleIdForCode(code, roles);
  return roleId != null
    ? { userRoleCode: code, userRoleId: roleId }
    : { userRoleCode: code };
};

type SectionCardProps = {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
};

const SectionCard = ({ title, subtitle, icon, children }: SectionCardProps) => (
  <Paper elevation={0} className="user-admin-section-card">
    <Stack spacing={2}>
      <Stack
        className="form-two-col-row"
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
      >
        <Box className="user-admin-section-icon">{icon}</Box>
        <Box>
          <Typography variant="subtitle1" className="user-admin-section-title">
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" className="user-admin-section-subtitle">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Stack spacing={2} className="user-admin-fields">
        {children}
      </Stack>
    </Stack>
  </Paper>
);

const UserAdminPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawTenant = Number(searchParams.get(SearchParams.TENANT));
  const rawProperty = Number(searchParams.get(SearchParams.PROPERTY));
  const defaultTenantId =
    Number.isFinite(rawTenant) &&
    rawTenant >= 1 &&
    rawTenant <= MAX_TENANT_PROPERTY_VALUE
      ? rawTenant
      : 1;
  const defaultPropertyId =
    Number.isFinite(rawProperty) &&
    rawProperty >= 1 &&
    rawProperty <= MAX_TENANT_PROPERTY_VALUE
      ? rawProperty
      : 1;
  const rawProductFromUrl = Number(searchParams.get(SearchParams.PRODUCT));
  const defaultProductId = PRODUCT_IDS.includes(rawProductFromUrl)
    ? rawProductFromUrl
    : ProductMap[ProductEnum.VERSA];

  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"create" | "edit">("create");

  const [customers, setCustomers] = useState<OnboardingCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");

  const showAlert = (message: string, type: "success" | "error") => {
    setAlertMessage(message);
    setAlertType(type);
    setOpenAlert(true);
  };

  const dashboardHref = useMemo(() => {
    const t = searchParams.get(SearchParams.TENANT) ?? String(defaultTenantId);
    const p =
      searchParams.get(SearchParams.PROPERTY) ?? String(defaultPropertyId);
    const pr =
      searchParams.get(SearchParams.PRODUCT) ?? String(defaultProductId);
    return `/?tenantId=${t}&propertyId=${p}&productId=${pr}&component=Property%20Information`;
  }, [searchParams, defaultTenantId, defaultPropertyId, defaultProductId]);

  const [customerRefreshKey, setCustomerRefreshKey] = useState(0);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const auth: { isAuthenticated?: boolean } = await checkAuthStatus();
        setAuthenticated(Boolean(auth?.isAuthenticated));
      } catch {
        setAuthenticated(false);
      } finally {
        setAuthChecked(true);
        setLoading(false);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    const load = async () => {
      setCustomersLoading(true);
      try {
        const res = await getCustomers();
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        if (!res.ok) {
          showAlert(
            typeof data === "string"
              ? data
              : `Could not load customers (${res.status})`,
            "error",
          );
          setCustomers([]);
          return;
        }
        setCustomers(parseCustomers(data));
      } catch (e: unknown) {
        if (!cancelled) {
          showAlert(
            e instanceof Error ? e.message : "Failed to load customers",
            "error",
          );
          setCustomers([]);
        }
      } finally {
        if (!cancelled) setCustomersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [authenticated, customerRefreshKey]);

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    const loadRoles = async () => {
      try {
        const res = await getUserRoles();
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        if (res.ok) {
          const parsed = parseUserRoles(data);
          if (parsed.length > 0) {
            setUserRoles(parsed);
          }
        }
      } catch {
        /* keep DEFAULT_USER_ROLES */
      }
    };
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  useEffect(() => {
    if (authChecked && !authenticated) {
      navigate("/login", { replace: true });
    }
  }, [authChecked, authenticated, navigate]);

  // —— Create user ——
  const [cCustomer, setCCustomer] = useState<OnboardingCustomer | null>(null);
  const [cUsername, setCUsername] = useState("");
  const [cPassword, setCPassword] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cFirst, setCFirst] = useState("");
  const [cLast, setCLast] = useState("");
  const [cRoleCode, setCRoleCode] = useState<string>(WorkflowRoleCode.CUSTOMER);
  const [cTenantId, setCTenantId] = useState(defaultTenantId);
  const [cPropertyId, setCPropertyId] = useState(defaultPropertyId);
  const [cProductIds, setCProductIds] = useState<number[]>([defaultProductId]);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [userRoles, setUserRoles] =
    useState<OnboardingUserRole[]>(DEFAULT_USER_ROLES);
  const [createShareSnapshot, setCreateShareSnapshot] =
    useState<ShareCredentialSnapshot | null>(null);
  const [credentialsEmailSending, setCredentialsEmailSending] = useState(false);

  const [createCustomerDialogOpen, setCreateCustomerDialogOpen] =
    useState(false);
  const [dialogIncludeUser, setDialogIncludeUser] = useState(false);
  const [dialogCustomer, setDialogCustomer] =
    useState<OnboardingCustomer>(emptyCustomerDraft);
  const [dialogSubmitting, setDialogSubmitting] = useState(false);
  const [showDialogPassword, setShowDialogPassword] = useState(false);
  const [dialogImplEmailsText, setDialogImplEmailsText] = useState("");
  const [eImplEmailsText, setEImplEmailsText] = useState("");
  const dialogCodeTouchedRef = useRef(false);

  const openCreateCustomerDialog = (includeUser: boolean) => {
    setDialogIncludeUser(includeUser);
    setDialogCustomer(emptyCustomerDraft());
    setDialogImplEmailsText("");
    dialogCodeTouchedRef.current = false;
    setCreateCustomerDialogOpen(true);
  };

  const patchDialogCustomer = (patch: Partial<OnboardingCustomer>) => {
    setDialogCustomer((prev) => ({ ...prev, ...patch }));
  };

  // —— Edit user ——
  const [allUsers, setAllUsers] = useState<OnboardingUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userRefreshKey, setUserRefreshKey] = useState(0);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<OnboardingUser | null>(null);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [eUserId, setEUserId] = useState<number | "">("");
  const [eUsername, setEUsername] = useState("");
  const [eFirst, setEFirst] = useState("");
  const [eLast, setELast] = useState("");
  const [eEmail, setEEmail] = useState("");
  const [eRoleCode, setERoleCode] = useState<string>(WorkflowRoleCode.CUSTOMER);
  const [eCustomer, setECustomer] = useState<OnboardingCustomer | null>(null);
  const [eTenantId, setETenantId] = useState(defaultTenantId);
  const [ePropertyId, setEPropertyId] = useState(defaultPropertyId);
  const [eProductIds, setEProductIds] = useState<number[]>([defaultProductId]);
  const [ePassword, setEPassword] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  const editCredentialSnapshot = useMemo((): ShareCredentialSnapshot | null => {
    if (!editFormOpen || !eUsername.trim() || !eEmail.trim() || !eCustomer) {
      return null;
    }
    if (!validateEmail(eEmail.trim())) return null;

    const customerForSnapshot = withImplementationEmailsFromText(
      eCustomer,
      eImplEmailsText,
    );
    return {
      username: eUsername.trim(),
      password: ePassword.trim() || "(unchanged - existing password not shown)",
      tenantId: eTenantId,
      propertyId: ePropertyId,
      productId: primaryProductId(eProductIds, defaultProductId),
      email: eEmail.trim(),
      firstName: eFirst.trim(),
      lastName: eLast.trim(),
      customerName:
        customerForSnapshot.name || customerForSnapshot.code || "Customer",
    };
  }, [
    editFormOpen,
    eUsername,
    eEmail,
    eCustomer,
    eImplEmailsText,
    ePassword,
    eTenantId,
    ePropertyId,
    eProductIds,
    eFirst,
    eLast,
    defaultProductId,
  ]);

  const canEmailEditCredentials = Boolean(
    editCredentialSnapshot &&
    editCredentialSnapshot.password &&
    !editCredentialSnapshot.password.startsWith("(unchanged"),
  );

  const filteredUsers = useMemo(
    () => allUsers.filter((u) => userMatchesSearch(u, userSearchQuery)),
    [allUsers, userSearchQuery],
  );

  const createCustomerOptions = useMemo(
    () => withSelectedCustomerInOptions(customers, cCustomer),
    [customers, cCustomer],
  );

  const editCustomerOptions = useMemo(
    () => withSelectedCustomerInOptions(customers, eCustomer),
    [customers, eCustomer],
  );

  useEffect(() => {
    setCCustomer((prev) => resolveCustomerFromList(prev, customers));
  }, [customers]);

  useEffect(() => {
    if (!editFormOpen) return;
    setECustomer((prev) => resolveCustomerFromList(prev, customers));
  }, [customers, editFormOpen]);

  useEffect(() => {
    setSelectedUser((prev) => {
      if (!prev) return prev;
      return allUsers.find((u) => u.id === prev.id) ?? prev;
    });
  }, [allUsers]);

  const loadAllUsers = async (): Promise<OnboardingUser[]> => {
    setUsersLoading(true);
    try {
      const res = await getOnboardingUsers();
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        showAlert(
          typeof data === "string"
            ? data
            : `Could not load users (${res.status})`,
          "error",
        );
        setAllUsers([]);
        return [];
      }
      const list = parseOnboardingUsers(data, userRoles);
      setAllUsers(list);
      return list;
    } catch (e: unknown) {
      showAlert(
        e instanceof Error ? e.message : "Failed to load users",
        "error",
      );
      setAllUsers([]);
      return [];
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== "edit" || !authenticated) return;
    loadAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when roles catalog is available
  }, [tab, authenticated, userRefreshKey, userRoles]);

  const resetEditForm = () => {
    setEditFormOpen(false);
    setEUserId("");
    setEUsername("");
    setEFirst("");
    setELast("");
    setEEmail("");
    setERoleCode(WorkflowRoleCode.CUSTOMER);
    setECustomer(null);
    setETenantId(defaultTenantId);
    setEPropertyId(defaultPropertyId);
    setEProductIds([defaultProductId]);
    setEPassword("");
    setEImplEmailsText("");
  };

  const populateEditForm = (u: OnboardingUser) => {
    const t0 = u.tenants[0];
    setEUserId(u.id);
    setEUsername(u.username);
    setEFirst(u.firstName);
    setELast(u.lastName);
    setEEmail(u.email);
    const roleCode = normalizeRoleCodeToCatalog(
      u.userRole?.code ?? u.userRoleCode ?? WorkflowRoleCode.CUSTOMER,
      userRoles,
    );
    setERoleCode(roleCode);
    const cust = mergeCustomerForEdit(u.customer, customers);
    setECustomer(cust);
    setEImplEmailsText(
      implementationTeamEmailsToFormValue(cust.implementationTeamEmails),
    );
    setETenantId(
      typeof t0?.tenantId === "number" ? t0.tenantId : defaultTenantId,
    );
    setEPropertyId(
      typeof t0?.propertyId === "number" ? t0.propertyId : defaultPropertyId,
    );
    const ids = productIdsFromTenants(u.tenants, PRODUCT_IDS);
    setEProductIds(ids.length > 0 ? ids : [defaultProductId]);
    setEPassword("");
  };

  const handleBeginEdit = () => {
    if (!selectedUser) {
      showAlert("Search and select a user first.", "error");
      return;
    }
    const current =
      allUsers.find((u) => u.id === selectedUser.id) ?? selectedUser;
    setSelectedUser(current);
    populateEditForm(current);
    setEditFormOpen(true);
  };

  const validateCustomerFields = (
    customer: OnboardingCustomer,
    implEmailsText: string,
  ): string | null => {
    const code = customer.code.trim();
    const name = customer.name.trim();
    const implementationTeamEmails =
      formValueToImplementationTeamEmails(implEmailsText);
    if (!code) return "Customer code is required.";
    if (/\s/.test(code)) return "Customer code cannot contain spaces.";
    if (!CUSTOMER_CODE_REGEX.test(code)) {
      return "Customer code may only contain letters, numbers, underscores, and hyphens.";
    }
    if (!name) return "Customer name is required.";
    if (!validateEmail(customer.email.trim())) {
      return "Enter a valid customer contact email.";
    }
    if (implementationTeamEmails.length === 0) {
      return "Enter at least one implementation team email.";
    }
    if (implementationTeamEmails.some((p) => !validateEmail(p))) {
      return "Each implementation team email must be a valid address.";
    }
    if (
      customer.technicalProjectManagerEmail.trim() &&
      !validateEmail(customer.technicalProjectManagerEmail.trim())
    ) {
      return "Enter a valid technical project manager email.";
    }
    if (
      customer.projectManagerEmail.trim() &&
      !validateEmail(customer.projectManagerEmail.trim())
    ) {
      return "Enter a valid project manager email.";
    }
    return null;
  };

  const validateUserCreateFields = (
    customer: OnboardingCustomer | null,
  ): string | null => {
    if (!customer) return "Select a customer from the list.";
    if (!customer.code.trim()) return "Selected customer is missing a code.";
    const user = cUsername.trim();
    if (!user) return "Username is required.";
    if (/\s/.test(cUsername)) return "Username cannot contain spaces.";
    if (!USERNAME_REGEX.test(user)) {
      return "Username may only contain letters, numbers, underscores, and hyphens.";
    }
    if (!cPassword) return "Password is required.";
    if (!validateEmail(cEmail.trim())) return "Enter a valid email address.";
    if (!cFirst.trim() || !cLast.trim())
      return "First and last name are required.";
    if (cFirst.trim().length > 100 || cLast.trim().length > 100) {
      return "First and last name must be at most 100 characters.";
    }
    if (cTenantId < 1 || cTenantId > MAX_TENANT_PROPERTY_VALUE) {
      return `Tenant ID must be between 1 and ${MAX_TENANT_PROPERTY_VALUE}.`;
    }
    if (cPropertyId < 1 || cPropertyId > MAX_TENANT_PROPERTY_VALUE) {
      return `Property ID must be between 1 and ${MAX_TENANT_PROPERTY_VALUE}.`;
    }
    if (cProductIds.length === 0) return "Select at least one product.";
    if (cProductIds.some((id) => !PRODUCT_IDS.includes(id))) {
      return "One or more selected products are invalid.";
    }
    return null;
  };

  const validateCreateFields = (): string | null =>
    validateUserCreateFields(cCustomer);

  const normalizedDialogCustomer = (): OnboardingCustomer =>
    withImplementationEmailsFromText(
      {
        ...dialogCustomer,
        code: dialogCustomer.code.trim(),
        name: dialogCustomer.name.trim(),
        email: dialogCustomer.email.trim(),
        technicalProjectManagerEmail:
          dialogCustomer.technicalProjectManagerEmail.trim(),
        projectManagerEmail: dialogCustomer.projectManagerEmail.trim(),
      },
      dialogImplEmailsText,
    );

  const handleDialogCreateCustomerAndUser = async () => {
    const customerErr = validateCustomerFields(
      dialogCustomer,
      dialogImplEmailsText,
    );
    if (customerErr) {
      showAlert(customerErr, "error");
      return;
    }

    const stagedCustomer = normalizedDialogCustomer();

    if (!dialogIncludeUser) {
      setCCustomer(stagedCustomer);
      setCustomers((prev) => upsertCustomerInList(prev, stagedCustomer));
      setCreateCustomerDialogOpen(false);
      showAlert(
        "Customer details are ready. Complete the user form below and click Create user — the customer is saved with that request.",
        "success",
      );
      return;
    }

    const userErr = validateUserCreateFields(stagedCustomer);
    if (userErr) {
      showAlert(userErr, "error");
      return;
    }

    const customerPayload = customerToCreatePayload(stagedCustomer);

    setDialogSubmitting(true);
    setCreateShareSnapshot(null);
    try {
      const res = await createOnboardingUser({
        user: {
          username: cUsername.trim(),
          password: cPassword,
          email: cEmail.trim(),
          firstName: cFirst.trim(),
          lastName: cLast.trim(),
          ...userRoleFieldsForCode(cRoleCode, userRoles),
        },
        tenantId: cTenantId,
        propertyId: cPropertyId,
        productIds: cProductIds,
        customer: customerPayload,
      });
      const { ok, status, parsed } = await parseApiResponseBody(res);
      if (!ok || !isSuccessStatus(status)) {
        showAlert(
          apiErrorMessage(parsed, status, "Create user failed"),
          "error",
        );
        return;
      }

      setCCustomer(stagedCustomer);
      setCustomers((prev) => upsertCustomerInList(prev, stagedCustomer));
      const shareSnapshot: ShareCredentialSnapshot = {
        username: cUsername.trim(),
        password: cPassword,
        tenantId: cTenantId,
        propertyId: cPropertyId,
        productId: primaryProductId(cProductIds, defaultProductId),
        email: cEmail.trim(),
        firstName: cFirst.trim(),
        lastName: cLast.trim(),
        customerName: stagedCustomer.name || stagedCustomer.code || "Customer",
      };
      setCreateShareSnapshot(shareSnapshot);
      const emailed = await sendCredentialsFromSnapshot(shareSnapshot, {
        notify: false,
      });
      showAlert(
        emailed
          ? `Customer and user created. Credentials were emailed to ${shareSnapshot.email}.`
          : "Customer and user created, but the credentials email could not be sent. Use Email credentials to retry.",
        emailed ? "success" : "error",
      );
      setCreateCustomerDialogOpen(false);
      setCUsername("");
      setCPassword("");
      setShowCreatePassword(false);
      setCEmail("");
      setCFirst("");
      setCLast("");
      setCustomerRefreshKey((k) => k + 1);
      setUserRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : "Create user failed", "error");
    } finally {
      setDialogSubmitting(false);
    }
  };

  const handleShareCreateCredentials = async () => {
    if (!createShareSnapshot) {
      showAlert(
        "Share is available after a successful create (HTTP 200).",
        "error",
      );
      return;
    }
    const ok = await copyTextToClipboard(
      shareSnapshotToText(createShareSnapshot),
    );
    showAlert(
      ok ? "Credentials copied to clipboard." : "Could not copy to clipboard.",
      ok ? "success" : "error",
    );
  };

  const handleShareEditCredentials = async () => {
    if (!editCredentialSnapshot) {
      showAlert("Open a user for editing with a valid email first.", "error");
      return;
    }
    const ok = await copyTextToClipboard(
      shareSnapshotToText(editCredentialSnapshot),
    );
    showAlert(
      ok ? "Credentials copied to clipboard." : "Could not copy to clipboard.",
      ok ? "success" : "error",
    );
  };

  const sendCredentialsFromSnapshot = async (
    snapshot: ShareCredentialSnapshot,
    options?: { notify?: boolean },
  ): Promise<boolean> => {
    const notify = options?.notify !== false;

    if (!snapshot.email.trim() || !validateEmail(snapshot.email.trim())) {
      if (notify) showAlert("A valid recipient email is required.", "error");
      return false;
    }
    if (snapshot.password.startsWith("(unchanged")) {
      if (notify)
        showAlert("Set a new password before emailing credentials.", "error");
      return false;
    }

    setCredentialsEmailSending(true);
    try {
      const { response, data } = await sendCredentialsEmail({
        toAddress: snapshot.email.trim(),
        firstName: snapshot.firstName,
        lastName: snapshot.lastName,
        username: snapshot.username,
        password: snapshot.password,
        customerName: snapshot.customerName,
        productId: snapshot.productId,
        tenantId: snapshot.tenantId,
        propertyId: snapshot.propertyId,
      });
      if (!response?.ok) {
        if (notify) {
          const message =
            typeof data === "object" && data !== null && "message" in data
              ? String((data as { message: unknown }).message)
              : "Failed to send credentials email.";
          showAlert(message, "error");
        }
        return false;
      }
      if (notify) {
        showAlert(
          `Credentials emailed to ${snapshot.email.trim()}.`,
          "success",
        );
      }
      return true;
    } catch (e: unknown) {
      if (notify) {
        showAlert(
          e instanceof Error ? e.message : "Failed to send credentials email.",
          "error",
        );
      }
      return false;
    } finally {
      setCredentialsEmailSending(false);
    }
  };

  const handleSendCreateCredentialsEmail = async () => {
    if (!createShareSnapshot) {
      showAlert("Send email is available after a successful create.", "error");
      return;
    }
    await sendCredentialsFromSnapshot(createShareSnapshot);
  };

  const handleSendEditCredentialsEmail = async () => {
    if (!editCredentialSnapshot) {
      showAlert("Open a user for editing with a valid email first.", "error");
      return;
    }
    if (!canEmailEditCredentials) {
      showAlert("Enter a new password before emailing credentials.", "error");
      return;
    }
    await sendCredentialsFromSnapshot(editCredentialSnapshot);
  };

  const handleCreate = async () => {
    const err = validateCreateFields();
    if (err) {
      showAlert(err, "error");
      return;
    }
    if (!cCustomer) return;
    setCreateSubmitting(true);
    setCreateShareSnapshot(null);
    try {
      const res = await createOnboardingUser({
        user: {
          username: cUsername.trim(),
          password: cPassword,
          email: cEmail.trim(),
          firstName: cFirst.trim(),
          lastName: cLast.trim(),
          ...userRoleFieldsForCode(cRoleCode, userRoles),
        },
        tenantId: cTenantId,
        propertyId: cPropertyId,
        productIds: cProductIds,
        customer: customerToCreatePayload(cCustomer),
      });
      const { ok, status, parsed } = await parseApiResponseBody(res);
      if (!ok || !isSuccessStatus(status)) {
        showAlert(apiErrorMessage(parsed, status, "Create failed"), "error");
        return;
      }
      const shareSnapshot: ShareCredentialSnapshot = {
        username: cUsername.trim(),
        password: cPassword,
        tenantId: cTenantId,
        propertyId: cPropertyId,
        productId: primaryProductId(cProductIds, defaultProductId),
        email: cEmail.trim(),
        firstName: cFirst.trim(),
        lastName: cLast.trim(),
        customerName: cCustomer.name || cCustomer.code || "Customer",
      };
      setCreateShareSnapshot(shareSnapshot);
      const emailed = await sendCredentialsFromSnapshot(shareSnapshot, {
        notify: false,
      });
      setCustomers((prev) => upsertCustomerInList(prev, cCustomer));
      showAlert(
        emailed
          ? `User created successfully. Credentials were emailed to ${shareSnapshot.email}.`
          : "User created successfully, but the credentials email could not be sent. Use Email credentials to retry.",
        emailed ? "success" : "error",
      );
      setCustomerRefreshKey((k) => k + 1);
      setCUsername("");
      setCPassword("");
      setShowCreatePassword(false);
      setCEmail("");
      setCFirst("");
      setCLast("");
    } catch (e: unknown) {
      showAlert(
        e instanceof Error ? e.message : "Create request failed",
        "error",
      );
    } finally {
      setCreateSubmitting(false);
    }
  };

  const validateEditFields = (): string | null => {
    if (!eCustomer?.code.trim()) return "Customer is required on update.";
    if (!validateEmail(eEmail.trim())) return "Enter a valid email address.";
    if (!validateEmail(eCustomer.email.trim())) {
      return "Enter a valid customer contact email.";
    }
    const implementationTeamEmails =
      formValueToImplementationTeamEmails(eImplEmailsText);
    if (implementationTeamEmails.length === 0) {
      return "Enter at least one implementation team email on the customer.";
    }
    if (implementationTeamEmails.some((p) => !validateEmail(p))) {
      return "Each implementation team email must be a valid address.";
    }
    if (!eFirst.trim() || !eLast.trim())
      return "First and last name are required.";
    if (eFirst.trim().length > 100 || eLast.trim().length > 100) {
      return "First and last name must be at most 100 characters.";
    }
    if (eTenantId < 1 || eTenantId > MAX_TENANT_PROPERTY_VALUE) {
      return `Tenant ID must be between 1 and ${MAX_TENANT_PROPERTY_VALUE}.`;
    }
    if (ePropertyId < 1 || ePropertyId > MAX_TENANT_PROPERTY_VALUE) {
      return `Property ID must be between 1 and ${MAX_TENANT_PROPERTY_VALUE}.`;
    }
    if (eProductIds.length === 0) return "Select at least one product.";
    if (eProductIds.some((id) => !PRODUCT_IDS.includes(id))) {
      return "One or more selected products are invalid.";
    }
    if (ePassword.trim()) {
      if (/\s/.test(ePassword)) return "Password cannot contain spaces.";
      if (ePassword.length > 128)
        return "Password must be at most 128 characters.";
    }
    return null;
  };

  const patchEditCustomer = (patch: Partial<OnboardingCustomer>) => {
    setECustomer((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const handleUpdate = async () => {
    const idNum = typeof eUserId === "number" ? eUserId : Number(eUserId);
    if (!idNum || Number.isNaN(idNum)) {
      showAlert("Select a user to update.", "error");
      return;
    }
    if (!eCustomer) return;
    const err = validateEditFields();
    if (err) {
      showAlert(err, "error");
      return;
    }
    const customerForUpdate = withImplementationEmailsFromText(
      eCustomer,
      eImplEmailsText,
    );

    setEditSubmitting(true);
    try {
      const payload: UpdateOnboardingUserBody = {
        firstName: eFirst.trim(),
        lastName: eLast.trim(),
        email: eEmail.trim(),
        ...userRoleFieldsForCode(eRoleCode, userRoles),
        tenantId: eTenantId,
        propertyId: ePropertyId,
        productIds: eProductIds,
        customer: customerToCreatePayload(customerForUpdate),
      };
      if (ePassword.trim()) {
        payload.password = ePassword;
      }
      const res = await updateOnboardingUser(idNum, payload);
      const bodyText = await res.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(bodyText);
      } catch {
        parsed = bodyText;
      }
      if (!res.ok || res.status !== 200) {
        showAlert(
          typeof parsed === "string" && parsed
            ? parsed
            : `Update failed (${res.status})`,
          "error",
        );
        return;
      }
      showAlert("User updated successfully.", "success");
      setEPassword("");
      setShowEditPassword(false);
      const list = await loadAllUsers();
      const refreshed = list.find((u) => u.id === idNum);
      if (refreshed) {
        setSelectedUser(refreshed);
        populateEditForm(refreshed);
        setEditFormOpen(true);
      } else {
        setUserRefreshKey((k) => k + 1);
      }
    } catch (e: unknown) {
      showAlert(
        e instanceof Error ? e.message : "Update request failed",
        "error",
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  if (loading || !authChecked) {
    return <PreLoader preloaderCheck />;
  }

  if (!authenticated) {
    return null;
  }

  return (
    <ThemeProvider theme={dashboardAppTheme}>
      <Box className="user-admin-page">
        <Box className="user-admin-shell">
          <Box className="user-admin-top-bar" component="header">
            <a
              href={dashboardHref}
              className="user-admin-logo-link"
              onClick={(e) => {
                e.preventDefault();
                navigate(dashboardHref);
              }}
              aria-label="Agilysys — back to dashboard"
            >
              <img
                src={AGILYSYS_LOGO_URL}
                alt="Agilysys"
                className="user-admin-logo"
              />
            </a>
            <Button
              variant="outlined"
              className="user-admin-back-btn"
              startIcon={<ArrowBack />}
              onClick={() => navigate(dashboardHref)}
            >
              Back to dashboard
            </Button>
          </Box>

          <Paper elevation={0} className="user-admin-hero">
            <Box>
              <Typography variant="h5" className="user-admin-hero-title">
                User administration
              </Typography>
              <Typography variant="body2" className="user-admin-hero-subtitle">
                Create onboarding accounts, assign customers, and manage tenant
                and product access.
              </Typography>
              <Stack
                className="form-checkbox-row form-two-col-row"
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                <Chip
                  size="small"
                  className="user-admin-stat-chip"
                  label={
                    customersLoading
                      ? "Loading customers…"
                      : `${customers.length} customer${customers.length === 1 ? "" : "s"}`
                  }
                />
                <Chip
                  size="small"
                  className="user-admin-stat-chip user-admin-stat-chip--accent"
                  label={
                    usersLoading && tab === "edit"
                      ? "Loading users…"
                      : `${allUsers.length} user${allUsers.length === 1 ? "" : "s"}`
                  }
                />
              </Stack>
            </Box>
          </Paper>

          <Paper elevation={0} className="user-admin-main-card">
            <Box className="user-admin-tabs-wrap">
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                className="user-admin-tabs"
              >
                <Tab
                  value="create"
                  label="Create user"
                  icon={<PersonAdd fontSize="small" />}
                  iconPosition="start"
                />
                <Tab
                  value="edit"
                  label="Edit user"
                  icon={<EditOutlined fontSize="small" />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {tab === "create" && (
              <Box className="user-admin-tab-panel">
                <Stack spacing={2.5}>
                  {!customersLoading && customers.length === 0 && (
                    <Alert severity="warning" className="user-admin-hint">
                      No customers yet. Use the dialog to enter customer and
                      user details — both are created together in one Create
                      user request.
                    </Alert>
                  )}
                  <SectionCard
                    title="Customer"
                    subtitle="Pick an existing customer or stage a new one. New customers are created when you submit Create user (same API request)."
                    icon={<Business fontSize="small" />}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "flex-start" }}
                    >
                      <Autocomplete
                        options={createCustomerOptions}
                        loading={customersLoading}
                        value={cCustomer}
                        onChange={(_, v) => setCCustomer(v)}
                        disabled={
                          customersLoading || createCustomerOptions.length === 0
                        }
                        getOptionLabel={(c) =>
                          c.code && c.name && c.code !== c.name
                            ? `${c.code} — ${c.name}`
                            : c.name || c.code
                        }
                        isOptionEqualToValue={customerMatches}
                        sx={{ flex: 1, minWidth: 0 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Customer"
                            placeholder="e.g. ACME — Acme Hotels"
                            required
                          />
                        )}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => openCreateCustomerDialog(false)}
                        disabled={customersLoading}
                        sx={{ flexShrink: 0 }}
                      >
                        Add new customer
                      </Button>
                      {customers.length === 0 && !cCustomer && (
                        <Button
                          variant="contained"
                          className="user-admin-primary-btn"
                          onClick={() => openCreateCustomerDialog(true)}
                          disabled={customersLoading}
                          sx={{ flexShrink: 0 }}
                        >
                          Create customer & user
                        </Button>
                      )}
                    </Stack>
                    {cCustomer && (
                      <Box className="user-admin-user-preview">
                        <Typography
                          variant="body2"
                          color="text.primary"
                          fontWeight={600}
                        >
                          {cCustomer.code &&
                          cCustomer.name &&
                          cCustomer.code !== cCustomer.name
                            ? `${cCustomer.code} — ${cCustomer.name}`
                            : cCustomer.name || cCustomer.code}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Contact: {cCustomer.email || "—"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Implementation team:{" "}
                          {implementationTeamEmailsToFormValue(
                            cCustomer.implementationTeamEmails,
                          ) || "—"}
                        </Typography>
                        {(cCustomer.technicalProjectManagerEmail ||
                          cCustomer.projectManagerEmail) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {[
                              cCustomer.technicalProjectManagerEmail &&
                                `TPM: ${cCustomer.technicalProjectManagerEmail}`,
                              cCustomer.projectManagerEmail &&
                                `PM: ${cCustomer.projectManagerEmail}`,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </SectionCard>

                  <SectionCard
                    title="Account credentials"
                    subtitle="Login username and password for the new onboarding user."
                    icon={<BadgeOutlined fontSize="small" />}
                  >
                    <TextField
                      label="Username"
                      value={cUsername}
                      onChange={(e) =>
                        setCUsername(e.target.value.replace(/\s/g, ""))
                      }
                      required
                      helperText="Letters, numbers, underscore, hyphen only — no spaces."
                      error={Boolean(
                        cUsername && !USERNAME_REGEX.test(cUsername.trim()),
                      )}
                    />
                    <Stack
                      className="user-admin-password-row"
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "flex-start" }}
                    >
                      <TextField
                        label="Password"
                        type={showCreatePassword ? "text" : "password"}
                        value={cPassword}
                        onChange={(e) => setCPassword(e.target.value)}
                        required
                        fullWidth
                        sx={{ flex: 1, minWidth: 0 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showCreatePassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                                edge="end"
                                size="small"
                                onClick={() => setShowCreatePassword((v) => !v)}
                              >
                                {showCreatePassword ? (
                                  <VisibilityOff fontSize="small" />
                                ) : (
                                  <Visibility fontSize="small" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setCPassword(generateAlphanumericPassword(8))
                        }
                        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                      >
                        Generate (8 chars)
                      </Button>
                    </Stack>
                    <TextField
                      label="Email"
                      value={cEmail}
                      onChange={(e) => setCEmail(e.target.value)}
                      required
                      error={Boolean(
                        cEmail.trim() && !validateEmail(cEmail.trim()),
                      )}
                      helperText={
                        cEmail.trim() && !validateEmail(cEmail.trim())
                          ? "Invalid email"
                          : undefined
                      }
                    />
                  </SectionCard>

                  <SectionCard
                    title="Profile"
                    subtitle="Display name and access role for this user."
                    icon={<PersonAdd fontSize="small" />}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="First name"
                        value={cFirst}
                        onChange={(e) => setCFirst(e.target.value)}
                        fullWidth
                        required
                        inputProps={{ maxLength: 100 }}
                      />
                      <TextField
                        label="Last name"
                        value={cLast}
                        onChange={(e) => setCLast(e.target.value)}
                        fullWidth
                        required
                        inputProps={{ maxLength: 100 }}
                      />
                    </Stack>
                    <TextField
                      select
                      label="Role"
                      value={cRoleCode}
                      onChange={(e) => setCRoleCode(e.target.value)}
                    >
                      {userRoles.map((o) => (
                        <MenuItem key={o.code} value={o.code}>
                          {o.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </SectionCard>

                  <SectionCard
                    title="Tenant & products"
                    subtitle="Tenant and property IDs plus one or more product licenses for this user."
                    icon={<Apartment fontSize="small" />}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Tenant ID"
                        value={cTenantId === 0 ? "" : String(cTenantId)}
                        onChange={(e) =>
                          setCTenantId(
                            parseTenantPropertyDigits(e.target.value),
                          )
                        }
                        fullWidth
                        inputProps={{
                          inputMode: "numeric",
                          maxLength: MAX_TENANT_PROPERTY_DIGITS,
                        }}
                        helperText={`Up to ${MAX_TENANT_PROPERTY_DIGITS} digits (1–${MAX_TENANT_PROPERTY_VALUE}).`}
                      />
                      <TextField
                        label="Property ID"
                        value={cPropertyId === 0 ? "" : String(cPropertyId)}
                        onChange={(e) =>
                          setCPropertyId(
                            parseTenantPropertyDigits(e.target.value),
                          )
                        }
                        fullWidth
                        inputProps={{
                          inputMode: "numeric",
                          maxLength: MAX_TENANT_PROPERTY_DIGITS,
                        }}
                        helperText={`Up to ${MAX_TENANT_PROPERTY_DIGITS} digits (1–${MAX_TENANT_PROPERTY_VALUE}).`}
                      />
                    </Stack>
                    <Autocomplete
                      multiple
                      options={productOptions}
                      value={productOptions.filter((o) =>
                        cProductIds.includes(o.value),
                      )}
                      onChange={(_, selected) =>
                        setCProductIds(selected.map((o) => o.value))
                      }
                      getOptionLabel={(o) => `${o.label} (${o.value})`}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      disableCloseOnSelect
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Products"
                          placeholder="Select one or more products"
                          required
                        />
                      )}
                    />
                  </SectionCard>

                  {createShareSnapshot && (
                    <Alert severity="success" className="user-admin-hint">
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        User created — credentials saved for sharing
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                      >
                        {shareSnapshotToText(createShareSnapshot)}
                      </Typography>
                    </Alert>
                  )}

                  <Box className="user-admin-actions-bar">
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                    >
                      <Button
                        variant="contained"
                        className="user-admin-primary-btn"
                        onClick={handleCreate}
                        disabled={createSubmitting || !cCustomer}
                        fullWidth
                      >
                        {createSubmitting
                          ? credentialsEmailSending
                            ? "Sending credentials…"
                            : "Creating…"
                          : "Create user"}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<EmailOutlined />}
                        onClick={handleSendCreateCredentialsEmail}
                        disabled={
                          createShareSnapshot === null ||
                          createSubmitting ||
                          credentialsEmailSending
                        }
                        fullWidth
                      >
                        {credentialsEmailSending
                          ? "Sending…"
                          : "Resend credentials"}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ShareIcon />}
                        onClick={handleShareCreateCredentials}
                        disabled={
                          createShareSnapshot === null || createSubmitting
                        }
                        fullWidth
                      >
                        Copy credentials
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            )}

            {tab === "edit" && (
              <Box className="user-admin-tab-panel">
                <Stack spacing={2.5}>
                  <SectionCard
                    title="Find a user"
                    subtitle="Search by username, email, name, or customer — then open the editor."
                    icon={<Search fontSize="small" />}
                  >
                    <TextField
                      label="Search users"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Username, email, name, or customer code"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Autocomplete
                      options={filteredUsers}
                      loading={usersLoading}
                      getOptionLabel={userOptionLabel}
                      value={selectedUser}
                      onChange={(_, v) => {
                        if (v?.id === selectedUser?.id) return;
                        setSelectedUser(v);
                        setEditFormOpen(false);
                        resetEditForm();
                      }}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="User"
                          placeholder="Select a user from search results"
                        />
                      )}
                    />
                    {selectedUser && (
                      <Box className="user-admin-user-preview">
                        <Typography
                          variant="body2"
                          color="text.primary"
                          fontWeight={600}
                        >
                          {userOptionLabel(selectedUser)}
                        </Typography>
                        {selectedUser.tenants.length > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            Tenants:{" "}
                            {selectedUser.tenants
                              .map(
                                (t) =>
                                  `tenant ${t.tenantId}, property ${t.propertyId}, product ${t.productId}`,
                              )
                              .join(" · ")}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        variant="contained"
                        className="user-admin-primary-btn"
                        onClick={handleBeginEdit}
                        disabled={!selectedUser || usersLoading}
                        fullWidth
                      >
                        Edit selected user
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearchQuery("");
                          resetEditForm();
                        }}
                        disabled={!selectedUser && !editFormOpen}
                        fullWidth
                      >
                        Clear selection
                      </Button>
                    </Stack>
                  </SectionCard>

                  {editFormOpen && selectedUser && eCustomer && (
                    <Stack spacing={2.5}>
                      <SectionCard
                        title="User details"
                        subtitle={`Editing user #${selectedUser.id}`}
                        icon={<BadgeOutlined fontSize="small" />}
                      >
                        <TextField
                          label="Username"
                          value={eUsername}
                          disabled
                          fullWidth
                        />
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            label="First name"
                            value={eFirst}
                            onChange={(e) => setEFirst(e.target.value)}
                            fullWidth
                            required
                            inputProps={{ maxLength: 100 }}
                          />
                          <TextField
                            label="Last name"
                            value={eLast}
                            onChange={(e) => setELast(e.target.value)}
                            fullWidth
                            required
                            inputProps={{ maxLength: 100 }}
                          />
                        </Stack>
                        <TextField
                          label="Email"
                          value={eEmail}
                          onChange={(e) => setEEmail(e.target.value)}
                          required
                          error={Boolean(
                            eEmail.trim() && !validateEmail(eEmail.trim()),
                          )}
                          helperText={
                            eEmail.trim() && !validateEmail(eEmail.trim())
                              ? "Invalid email"
                              : undefined
                          }
                        />
                        <TextField
                          select
                          label="Role"
                          value={eRoleCode}
                          onChange={(e) => setERoleCode(e.target.value)}
                        >
                          {userRoles.map((o) => (
                            <MenuItem key={o.code} value={o.code}>
                              {o.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ sm: "flex-start" }}
                        >
                          <TextField
                            label="New password (optional)"
                            type={showEditPassword ? "text" : "password"}
                            value={ePassword}
                            onChange={(e) => setEPassword(e.target.value)}
                            helperText="Leave blank to keep the current password."
                            fullWidth
                            error={Boolean(ePassword && /\s/.test(ePassword))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={
                                      showEditPassword
                                        ? "Hide password"
                                        : "Show password"
                                    }
                                    edge="end"
                                    size="small"
                                    onClick={() =>
                                      setShowEditPassword((v) => !v)
                                    }
                                  >
                                    {showEditPassword ? (
                                      <VisibilityOff fontSize="small" />
                                    ) : (
                                      <Visibility fontSize="small" />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              setEPassword(generateAlphanumericPassword(8))
                            }
                            sx={{ flexShrink: 0 }}
                          >
                            Generate (8 chars)
                          </Button>
                        </Stack>
                      </SectionCard>

                      <SectionCard
                        title="Customer"
                        subtitle="Organization linked to this user and notification contacts."
                        icon={<Business fontSize="small" />}
                      >
                        <Autocomplete
                          options={editCustomerOptions}
                          loading={customersLoading}
                          value={eCustomer}
                          onChange={(_, v) => {
                            if (!v) return;
                            setECustomer(v);
                            setEImplEmailsText(
                              implementationTeamEmailsToFormValue(
                                v.implementationTeamEmails,
                              ),
                            );
                          }}
                          getOptionLabel={(c) =>
                            c.code && c.name && c.code !== c.name
                              ? `${c.code} — ${c.name}`
                              : c.name || c.code
                          }
                          isOptionEqualToValue={customerMatches}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Customer"
                              InputLabelProps={{ required: true }}
                            />
                          )}
                        />
                        <TextField
                          label="Customer contact email"
                          value={eCustomer.email}
                          onChange={(e) =>
                            patchEditCustomer({ email: e.target.value })
                          }
                          required
                          error={Boolean(
                            eCustomer.email.trim() &&
                            !validateEmail(eCustomer.email.trim()),
                          )}
                        />
                        <TextField
                          label="Implementation team emails"
                          value={eImplEmailsText}
                          onChange={(e) => setEImplEmailsText(e.target.value)}
                          type="text"
                          inputProps={{ autoComplete: "off" }}
                          InputLabelProps={{ required: true }}
                          helperText="Comma-separated addresses"
                        />
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            label="Technical PM email"
                            value={eCustomer.technicalProjectManagerEmail}
                            onChange={(e) =>
                              patchEditCustomer({
                                technicalProjectManagerEmail: e.target.value,
                              })
                            }
                            fullWidth
                          />
                          <TextField
                            label="Project manager email"
                            value={eCustomer.projectManagerEmail}
                            onChange={(e) =>
                              patchEditCustomer({
                                projectManagerEmail: e.target.value,
                              })
                            }
                            fullWidth
                          />
                        </Stack>
                      </SectionCard>

                      <SectionCard
                        title="Tenant & products"
                        subtitle="Update tenant, property, and product access for this user."
                        icon={<Apartment fontSize="small" />}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            label="Tenant ID"
                            value={eTenantId === 0 ? "" : String(eTenantId)}
                            onChange={(e) =>
                              setETenantId(
                                parseTenantPropertyDigits(e.target.value),
                              )
                            }
                            fullWidth
                            inputProps={{
                              inputMode: "numeric",
                              maxLength: MAX_TENANT_PROPERTY_DIGITS,
                            }}
                          />
                          <TextField
                            label="Property ID"
                            value={ePropertyId === 0 ? "" : String(ePropertyId)}
                            onChange={(e) =>
                              setEPropertyId(
                                parseTenantPropertyDigits(e.target.value),
                              )
                            }
                            fullWidth
                            inputProps={{
                              inputMode: "numeric",
                              maxLength: MAX_TENANT_PROPERTY_DIGITS,
                            }}
                          />
                        </Stack>
                        <Autocomplete
                          multiple
                          options={productOptions}
                          value={productOptions.filter((o) =>
                            eProductIds.includes(o.value),
                          )}
                          onChange={(_, selected) =>
                            setEProductIds(selected.map((o) => o.value))
                          }
                          getOptionLabel={(o) => `${o.label} (${o.value})`}
                          isOptionEqualToValue={(a, b) => a.value === b.value}
                          disableCloseOnSelect
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Products"
                              placeholder="Select one or more products"
                              required
                            />
                          )}
                        />
                      </SectionCard>

                      <Box className="user-admin-actions-bar">
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                        >
                          <Button
                            variant="contained"
                            className="user-admin-primary-btn"
                            onClick={handleUpdate}
                            disabled={editSubmitting}
                            fullWidth
                          >
                            {editSubmitting ? "Saving…" : "Save changes"}
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<EmailOutlined />}
                            onClick={handleSendEditCredentialsEmail}
                            disabled={
                              !canEmailEditCredentials ||
                              editSubmitting ||
                              credentialsEmailSending
                            }
                            fullWidth
                          >
                            {credentialsEmailSending
                              ? "Sending…"
                              : "Email credentials"}
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            onClick={handleShareEditCredentials}
                            disabled={!editCredentialSnapshot || editSubmitting}
                            fullWidth
                          >
                            Copy credentials
                          </Button>
                          <Button
                            variant="text"
                            onClick={() => {
                              setEditFormOpen(false);
                              resetEditForm();
                            }}
                            disabled={editSubmitting}
                            fullWidth
                          >
                            Cancel edit
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}
          </Paper>
        </Box>

        <Dialog
          open={createCustomerDialogOpen}
          onClose={() => {
            if (!dialogSubmitting) setCreateCustomerDialogOpen(false);
          }}
          fullWidth
          maxWidth="sm"
          className="user-admin-dialog"
        >
          <DialogTitle className="user-admin-dialog-title">
            {dialogIncludeUser
              ? "Create customer and user"
              : "New customer details"}
          </DialogTitle>
          <DialogContent>
            <Box
              component="form"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                handleDialogCreateCustomerAndUser();
              }}
            >
              <Stack spacing={2.5} className="user-admin-fields">
                <SectionCard
                  title="Customer"
                  icon={<Business fontSize="small" />}
                >
                  <TextField
                    label="Customer name"
                    value={dialogCustomer.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      patchDialogCustomer({
                        name,
                        ...(dialogCodeTouchedRef.current
                          ? {}
                          : { code: generateCustomerCodeFromName(name) }),
                      });
                    }}
                    InputLabelProps={{ required: true }}
                  />
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "flex-start" }}
                  >
                    <TextField
                      label="Customer code"
                      value={dialogCustomer.code}
                      onChange={(e) => {
                        dialogCodeTouchedRef.current = true;
                        patchDialogCustomer({
                          code: e.target.value.replace(/\s/g, ""),
                        });
                      }}
                      InputLabelProps={{ required: true }}
                      helperText="Auto-generated from name — edit or regenerate."
                      fullWidth
                      error={Boolean(
                        dialogCustomer.code &&
                        !CUSTOMER_CODE_REGEX.test(dialogCustomer.code.trim()),
                      )}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ flexShrink: 0, mt: { sm: 1 } }}
                      disabled={!dialogCustomer.name.trim()}
                      onClick={() => {
                        dialogCodeTouchedRef.current = true;
                        patchDialogCustomer({
                          code: generateCustomerCodeFromName(
                            dialogCustomer.name,
                          ),
                        });
                      }}
                    >
                      Regenerate
                    </Button>
                  </Stack>
                  <TextField
                    label="Customer contact email"
                    value={dialogCustomer.email}
                    onChange={(e) =>
                      patchDialogCustomer({ email: e.target.value })
                    }
                    type="email"
                    InputLabelProps={{ required: true }}
                    error={Boolean(
                      dialogCustomer.email.trim() &&
                      !validateEmail(dialogCustomer.email.trim()),
                    )}
                  />
                  <TextField
                    label="Implementation team emails"
                    value={dialogImplEmailsText}
                    onChange={(e) => setDialogImplEmailsText(e.target.value)}
                    type="text"
                    inputProps={{ autoComplete: "off" }}
                    InputLabelProps={{ required: true }}
                    helperText="Comma-separated addresses"
                  />
                  <TextField
                    label="Technical PM email"
                    value={dialogCustomer.technicalProjectManagerEmail}
                    onChange={(e) =>
                      patchDialogCustomer({
                        technicalProjectManagerEmail: e.target.value,
                      })
                    }
                  />
                  <TextField
                    label="Project manager email"
                    value={dialogCustomer.projectManagerEmail}
                    onChange={(e) =>
                      patchDialogCustomer({
                        projectManagerEmail: e.target.value,
                      })
                    }
                  />
                </SectionCard>

                {dialogIncludeUser && (
                  <>
                    <SectionCard
                      title="User account"
                      icon={<PersonAdd fontSize="small" />}
                    >
                      <TextField
                        label="Username"
                        value={cUsername}
                        onChange={(e) =>
                          setCUsername(e.target.value.replace(/\s/g, ""))
                        }
                        InputLabelProps={{ required: true }}
                        error={Boolean(
                          cUsername && !USERNAME_REGEX.test(cUsername.trim()),
                        )}
                      />
                      <Stack
                        className="user-admin-password-row"
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ sm: "flex-start" }}
                      >
                        <TextField
                          label="Password"
                          type={showDialogPassword ? "text" : "password"}
                          value={cPassword}
                          onChange={(e) => setCPassword(e.target.value)}
                          InputLabelProps={{ required: true }}
                          fullWidth
                          sx={{ flex: 1, minWidth: 0 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label={
                                    showDialogPassword
                                      ? "Hide password"
                                      : "Show password"
                                  }
                                  edge="end"
                                  size="small"
                                  onClick={() =>
                                    setShowDialogPassword((v) => !v)
                                  }
                                >
                                  {showDialogPassword ? (
                                    <VisibilityOff fontSize="small" />
                                  ) : (
                                    <Visibility fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setCPassword(generateAlphanumericPassword(8))
                          }
                          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                        >
                          Generate (8 chars)
                        </Button>
                      </Stack>
                      <TextField
                        label="Email"
                        value={cEmail}
                        onChange={(e) => setCEmail(e.target.value)}
                        type="email"
                        InputLabelProps={{ required: true }}
                        error={Boolean(
                          cEmail.trim() && !validateEmail(cEmail.trim()),
                        )}
                      />
                      <Stack
                        className="form-two-col-row"
                        direction="row"
                        spacing={2}
                      >
                        <TextField
                          label="First name"
                          value={cFirst}
                          onChange={(e) => setCFirst(e.target.value)}
                          fullWidth
                          InputLabelProps={{ required: true }}
                          inputProps={{ maxLength: 100 }}
                        />
                        <TextField
                          label="Last name"
                          value={cLast}
                          onChange={(e) => setELast(e.target.value)}
                          fullWidth
                          InputLabelProps={{ required: true }}
                          inputProps={{ maxLength: 100 }}
                        />
                      </Stack>
                      <TextField
                        select
                        label="Role"
                        value={cRoleCode}
                        onChange={(e) => setCRoleCode(e.target.value)}
                        fullWidth
                      >
                        {userRoles.map((o) => (
                          <MenuItem key={o.code} value={o.code}>
                            {o.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </SectionCard>
                    <SectionCard
                      title="Tenant & products"
                      icon={<Apartment fontSize="small" />}
                    >
                      <Stack
                        className="form-two-col-row"
                        direction="row"
                        spacing={2}
                      >
                        <TextField
                          label="Tenant ID"
                          value={cTenantId === 0 ? "" : String(cTenantId)}
                          onChange={(e) =>
                            setCTenantId(
                              parseTenantPropertyDigits(e.target.value),
                            )
                          }
                          fullWidth
                          inputProps={{
                            inputMode: "numeric",
                            maxLength: MAX_TENANT_PROPERTY_DIGITS,
                          }}
                        />
                        <TextField
                          label="Property ID"
                          value={cPropertyId === 0 ? "" : String(cPropertyId)}
                          onChange={(e) =>
                            setCPropertyId(
                              parseTenantPropertyDigits(e.target.value),
                            )
                          }
                          fullWidth
                          inputProps={{
                            inputMode: "numeric",
                            maxLength: MAX_TENANT_PROPERTY_DIGITS,
                          }}
                        />
                      </Stack>
                      <Autocomplete
                        multiple
                        options={productOptions}
                        value={productOptions.filter((o) =>
                          cProductIds.includes(o.value),
                        )}
                        onChange={(_, selected) =>
                          setCProductIds(selected.map((o) => o.value))
                        }
                        getOptionLabel={(o) => `${o.label} (${o.value})`}
                        isOptionEqualToValue={(a, b) => a.value === b.value}
                        disableCloseOnSelect
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Products"
                            InputLabelProps={{ required: true }}
                          />
                        )}
                      />
                    </SectionCard>
                  </>
                )}
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions className="user-admin-dialog-actions">
            <Button
              type="button"
              onClick={() => setCreateCustomerDialogOpen(false)}
              disabled={dialogSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              className="user-admin-primary-btn"
              onClick={handleDialogCreateCustomerAndUser}
              disabled={dialogSubmitting}
            >
              {dialogSubmitting
                ? "Creating…"
                : dialogIncludeUser
                  ? "Create customer and user"
                  : "Apply customer details"}
            </Button>
          </DialogActions>
        </Dialog>

        <AlertComponent
          message={alertMessage}
          type={alertType}
          alertOpen={openAlert}
          closeAlert={() => setOpenAlert(false)}
        />
      </Box>
    </ThemeProvider>
  );
};

export default UserAdminPage;
